/**
 * Custom hook for FingerprintJS Pro device verification.
 * Loads the FP.js agent on-demand (not on page load) to avoid
 * hundreds of sandboxed iframe errors in the browser console.
 * Returns requestId for server-side verification via the Events API.
 *
 * Throws on error — callers must handle failures (e.g. via mutation onError).
 */

import FingerprintJS, { type Agent } from '@fingerprintjs/fingerprintjs-pro';

let agentPromise: Promise<Agent> | null = null;

function getAgent(): Promise<Agent> {
  if (!agentPromise) {
    const loadOptions: { apiKey: string; endpoint?: string; scriptUrlPattern?: string } = {
      apiKey: import.meta.env.VITE_FPJS_API_KEY,
    };
    // Use Cloudflare proxy if configured (anti-adblock, first-party cookies)
    if (import.meta.env.VITE_FPJS_SCRIPT_URL) {
      loadOptions.scriptUrlPattern = import.meta.env.VITE_FPJS_SCRIPT_URL;
    }
    if (import.meta.env.VITE_FPJS_ENDPOINT) {
      loadOptions.endpoint = import.meta.env.VITE_FPJS_ENDPOINT;
    }
    agentPromise = FingerprintJS.load(loadOptions).catch((error) => {
      // Clear cached promise on failure so next call retries the load
      // (handles overnight tab hibernation, transient CDN failures, etc.)
      agentPromise = null;
      throw error;
    });
  }
  return agentPromise;
}

export function useFingerprint() {
  async function getFingerprint(): Promise<{ requestId: string }> {
    if (!import.meta.env.VITE_FPJS_API_KEY) {
      throw new Error('Device verification is not available. Please try again later.');
    }
    const agent = await getAgent();
    const result = await agent.get({ extendedResult: true });
    return { requestId: result.requestId };
  }

  return { getFingerprint };
}
